import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Facebook,
  Instagram,
  MessageCircle,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const TikTokIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" aria-hidden="true">
    <path
      d="M16 3v3.2A4.8 4.8 0 0 0 20.8 11V14a7.7 7.7 0 0 1-4.8-1.6V17a5.5 5.5 0 1 1-5.5-5.5c.3 0 .6 0 .9.1v3.2a2.4 2.4 0 1 0 1.6 2.2V3H16Z"
      fill="currentColor"
    />
  </svg>
);

const WhatsAppGlyph = ({ className = "h-6 w-6" }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.001 3.2c-7.073 0-12.8 5.726-12.8 12.8 0 2.255.594 4.452 1.722 6.392L3.2 28.8l6.589-1.722a12.74 12.74 0 0 0 6.212 1.585h.005c7.069 0 12.794-5.725 12.794-12.797 0-3.42-1.331-6.633-3.748-9.05A12.71 12.71 0 0 0 16.001 3.2zm0 23.428h-.004a10.62 10.62 0 0 1-5.412-1.482l-.388-.231-4.022 1.054 1.073-3.92-.253-.402a10.61 10.61 0 0 1-1.625-5.647c0-5.873 4.78-10.65 10.654-10.65 2.846 0 5.52 1.108 7.531 3.121a10.59 10.59 0 0 1 3.118 7.532c0 5.873-4.78 10.65-10.658 10.65zm5.838-7.978c-.32-.16-1.892-.933-2.184-1.04-.293-.107-.506-.16-.72.16s-.825 1.04-1.011 1.253c-.187.213-.373.24-.693.08-.32-.16-1.351-.499-2.574-1.59-.951-.849-1.594-1.898-1.78-2.218-.187-.32-.02-.493.14-.652.144-.143.32-.373.48-.56.16-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.72-1.733-.987-2.373-.26-.624-.524-.539-.72-.549-.187-.009-.4-.011-.613-.011-.213 0-.56.08-.853.4-.293.32-1.12 1.094-1.12 2.667 0 1.573 1.146 3.094 1.306 3.307.16.213 2.255 3.444 5.466 4.831.764.33 1.36.527 1.825.674.766.244 1.464.21 2.015.127.615-.092 1.892-.773 2.158-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.613-.373z" />
  </svg>
);

const Contact = () => {
  const { t, lang } = useLanguage();

  // Phone routing per language
  const phoneNumber = lang === "fa" ? "+4917657773450" : "+491781647873";
  const waNumber = lang === "fa" ? "4917657773450" : "491781647873";

  // Pre-filled WhatsApp message per language
  const waMessages = {
    ar: "السلام عليكم، أرغب في الحصول على استشارة من مكتب كومباس.",
    de: "Hallo Kompass, ich möchte gerne eine Beratung anfragen.",
    en: "Hello Kompass, I would like to request a consultation.",
    fa: "سلام، می‌خواهم درخواست مشاوره از دفتر کمپاس داشته باشم.",
  };
  const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(
    waMessages[lang] || waMessages.en
  )}`;

  // CTA labels
  const ctaLabel = {
    ar: "تحدث معنا الآن على واتساب",
    de: "Jetzt auf WhatsApp schreiben",
    en: "Chat with us on WhatsApp",
    fa: "همین حالا در واتساپ گفتگو کنید",
  }[lang] || "Chat with us on WhatsApp";

  const ctaSub = {
    ar: "ردّ سريع · مكالمة أو رسالة · بلغتك",
    de: "Schnelle Antwort · Anruf oder Nachricht · in Ihrer Sprache",
    en: "Fast reply · Call or message · in your language",
    fa: "پاسخ سریع · تماس یا پیام · به زبان شما",
  }[lang];

  const infoItems = [
    {
      icon: MapPin,
      label: t.contact.info.address_label,
      value: t.contact.info.address,
      href: "https://maps.google.com/?q=Eppelheimer+Str.+21,+69115+Heidelberg",
      testid: "contact-address",
    },
    {
      icon: Phone,
      label: t.contact.info.phone_label,
      value: phoneNumber,
      href: `tel:${phoneNumber.replace(/\s/g, "")}`,
      testid: "contact-phone-link",
    },
    {
      icon: Mail,
      label: t.contact.info.email_label,
      value: t.contact.info.email,
      href: "mailto:info@kompass-heidelberg.de",
      testid: "contact-email-link",
    },
    {
      icon: Globe,
      label: t.contact.info.web_label,
      value: t.contact.info.web,
      href: "https://www.kompass-heidelberg.de",
      testid: "contact-web-link",
    },
    {
      icon: Clock,
      label: t.contact.info.hours_label,
      value: t.contact.info.hours,
      href: null,
      testid: "contact-hours",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-8 md:py-12 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 60% 70% at 0% 100%, rgba(31,107,122,0.10) 0%, transparent 60%), radial-gradient(ellipse 70% 60% at 100% 0%, rgba(44,122,140,0.08) 0%, transparent 60%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <span className="eyebrow" data-testid="contact-eyebrow">
            {t.contact.eyebrow}
          </span>
          <h2 className="display display-2 mt-4 text-[color:var(--kompass-ink)]">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-base md:text-lg leading-relaxed text-[color:var(--kompass-ink-soft)]">
            {t.contact.lead}
          </p>
        </motion.div>

        <div className="mt-12 grid gap-8 lg:grid-cols-12">
          {/* WhatsApp CTA Card */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 relative overflow-hidden rounded-3xl p-8 md:p-12 shadow-[0_24px_60px_-30px_rgba(31,107,122,0.35)]"
            style={{
              background:
                "linear-gradient(135deg, #128C7E 0%, #25D366 70%, #25D366 100%)",
            }}
          >
            <div
              aria-hidden
              className="absolute -top-24 -end-20 h-72 w-72 rounded-full"
              style={{ background: "rgba(255,255,255,0.12)", filter: "blur(2px)" }}
            />
            <div
              aria-hidden
              className="absolute -bottom-32 -start-10 h-80 w-80 rounded-full"
              style={{ background: "rgba(0,0,0,0.08)", filter: "blur(4px)" }}
            />

            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
                  <WhatsAppGlyph className="h-7 w-7 text-white" />
                </span>
                <span className="text-xs uppercase tracking-[0.22em] text-white/80 font-semibold">
                  WhatsApp
                </span>
              </div>

              <h3 className="display text-3xl md:text-4xl font-semibold leading-tight text-white max-w-lg">
                {ctaLabel}
              </h3>
              <p className="mt-3 text-white/85 text-sm md:text-base">{ctaSub}</p>

              <div className="mt-8 flex flex-wrap gap-3 items-center">
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="whatsapp-cta-primary"
                  className="inline-flex items-center gap-2.5 rounded-full bg-white text-[#0c5e54] px-7 py-4 text-sm font-bold shadow-[0_10px_30px_-10px_rgba(0,0,0,0.4)] hover:bg-[#FDFBF7] hover:translate-y-[-2px] transition-all"
                >
                  <WhatsAppGlyph className="h-5 w-5" />
                  <span dir="ltr" className="font-mono text-[13px] font-semibold">
                    {phoneNumber}
                  </span>
                </a>
              </div>

              <div className="mt-8 pt-6 border-t border-white/20">
                <div className="text-[11px] uppercase tracking-widest text-white/70 mb-3">
                  Social
                </div>
                <div className="flex items-center gap-2.5">
                  {[
                    { Icon: Facebook, href: "#facebook", label: "Facebook", id: "social-facebook" },
                    { Icon: Instagram, href: "#instagram", label: "Instagram", id: "social-instagram" },
                    { Icon: TikTokIcon, href: "#tiktok", label: "TikTok", id: "social-tiktok" },
                    { Icon: MessageCircle, href: waUrl, label: "WhatsApp", id: "social-whatsapp" },
                  ].map(({ Icon, href, label, id }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                      data-testid={id}
                      className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white transition-colors"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Info column */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5"
          >
            <div className="rounded-3xl bg-white border border-[rgba(31,107,122,0.12)] p-7 md:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[color:var(--kompass-primary)] font-semibold">
                Kompass Heidelberg
              </div>
              <div className="mt-1 text-xl font-semibold text-[color:var(--kompass-ink)]">
                {t.contact.eyebrow}
              </div>

              <ul className="mt-7 space-y-5">
                {infoItems.map((it, i) => {
                  const Icon = it.icon;
                  const inner = (
                    <div className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                        style={{ background: "rgba(31,107,122,0.1)" }}
                      >
                        <Icon
                          className="h-4 w-4"
                          strokeWidth={1.7}
                          style={{ color: "var(--kompass-primary)" }}
                        />
                      </span>
                      <div>
                        <div className="text-[11px] uppercase tracking-widest text-[color:var(--kompass-muted)]">
                          {it.label}
                        </div>
                        <div
                          className="text-sm md:text-base mt-0.5 leading-relaxed text-[color:var(--kompass-ink)]"
                          dir={it.icon === Phone ? "ltr" : undefined}
                        >
                          {it.value}
                        </div>
                      </div>
                    </div>
                  );
                  return (
                    <li key={i}>
                      {it.href ? (
                        <a
                          href={it.href}
                          target={it.href.startsWith("http") ? "_blank" : undefined}
                          rel="noreferrer"
                          data-testid={it.testid}
                          className="block hover:text-[color:var(--kompass-primary)] transition-colors"
                        >
                          {inner}
                        </a>
                      ) : (
                        <div data-testid={it.testid}>{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
